import React, { useEffect, useMemo } from 'react';
import { ButtonV2, Box, TextV1 } from '@withjoy/joykit';
import { SpacingStack } from '@withjoy/joykit';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { StyledFlex, StyledButtonFlex, StyledLink, StyledText } from './PageNote.styles';
import { Divider } from '../';
import { usePageNoteController } from './PageNote.controller';
import { LazyDescriptionRichTextEditor } from '@shared/components/DescriptionRichTextEditor/DescriptionRichTextEditor.lazy';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { buildFontCategoryMap } from '@shared/utils/fonts/buildFontCategoryMap';

export interface PageNoteProps
  extends Readonly<{
    isMobile?: boolean;
    inDialog?: boolean;
    pageNote?: string | null;
    registryPageId?: string;
    // PRNT-4193 — when set, save through `updatePageContainer` on this id instead
    // of `updateEventPage` on the legacy registry row. See PageNote.controller.tsx.
    migratedPageContainerId?: string;
    // Used by the controller's cache eviction after migrated saves
    // (`GetRegistryOnboardingSuggestions` reads via `eventById`, not `eventByName`).
    eventId?: string;
    onClose?: () => void;
    eventHandle: string;
    onEditPageNote?(): void;
  }> {}

const PageNote: React.FC<PageNoteProps> = ({
  isMobile = false,
  inDialog = false,
  registryPageId,
  migratedPageContainerId,
  eventId,
  pageNote,
  onClose,
  eventHandle,
  onEditPageNote,
}) => {
  const { pageNoteFormik, updateRegistryNote, pageNoteIsDisabled, pageNoteUpdateLoading, updateSuccess } = usePageNoteController(
    eventHandle,
    pageNote,
    registryPageId,
    onEditPageNote,
    migratedPageContainerId,
    eventId,
  );
  const { getPageNoteTranslations } = useSharedRegistryTranslations();
  const { title, subtitle, cancel, save } = getPageNoteTranslations();

  // `false` skips the strict-provider check so `PageNote` keeps working in admin contexts
  // (sidebar, help links) that don't mount EventDesignThemingProvider — there we fall back
  // to the editor's defaults. On `/registry`, the provider is mounted by GuestRegistry so
  // the editor inherits the same fonts/typography the rendered page uses.
  const themingContext = useEventDesignThemingProvider(false);
  const { theme, eventDesign } = themingContext ?? {};
  const fontMap = useMemo(() => buildFontCategoryMap(eventDesign?.fonts), [eventDesign?.fonts]);

  const cancelChanges = () => {
    onClose?.();
    pageNoteFormik.resetForm();
  };

  useEffect(() => {
    if (updateSuccess) {
      onClose?.();
    }
  }, [updateSuccess, onClose]);

  const isCancelButtonVisible = inDialog || (!pageNoteIsDisabled && !pageNoteUpdateLoading);
  const stackSpacing = isMobile ? 6 : 7;
  return (
    <Box>
      <SpacingStack spacing={stackSpacing}>
        {!inDialog && (
          <SpacingStack spacing={3}>
            <TextV1
              component="h1"
              variant="hed4"
            >
              {title}
            </TextV1>
            <TextV1
              component="p"
              variant="body1"
            >
              {subtitle}
            </TextV1>
          </SpacingStack>
        )}
        <LazyDescriptionRichTextEditor
          content={pageNoteFormik.values.note}
          placeholder=""
          onChange={value => {
            pageNoteFormik.setFieldValue('note', value);
            pageNoteFormik.setFieldTouched('note', true, false);
          }}
          theme={theme}
          eventDesign={eventDesign}
          fontMap={fontMap}
        />
        <Divider margin="32px 0px 0px" />
      </SpacingStack>
      <StyledFlex
        alignItems="center"
        justifyContent="flex-end"
        isMobile={isMobile}
        marginTop={[3, null, 7]}
      >
        <StyledButtonFlex isMobile={isMobile}>
          {isCancelButtonVisible && (
            <Box
              marginRight={[3, null, 20]}
              style={{ alignSelf: 'center' }}
            >
              <StyledLink
                component="button"
                intent="secondary"
                underline="none"
                onClick={cancelChanges}
              >
                {cancel}
              </StyledLink>
            </Box>
          )}
          <ButtonV2
            disabled={pageNoteIsDisabled}
            onClick={updateRegistryNote}
            loading={pageNoteUpdateLoading}
          >
            <StyledText>{save}</StyledText>
          </ButtonV2>
        </StyledButtonFlex>
      </StyledFlex>
    </Box>
  );
};

PageNote.displayName = 'PageNote';

export { PageNote };
